'use client'
import { FC, useCallback } from 'react'
import { ProductDetailPageStd } from 'storefront-modules/products'
import { useAppConfigContext } from '#/src/appConfig'
import { useCustomCart } from '#/src/custom/cart/useCustomCart'
import { useModal } from '#/src/custom/controllers'
import { useSelector } from 'shared-redux'
import { selectWindowState } from 'shared-redux/state'
import useProductRequest from '#/src/custom/products/useProductRequest'
import { ProductMedia } from 'ecosystem'
import { IModalPhotoGalleryProps } from '#/src/custom/controllers/modals/ModalPhotoGallery'
import { InferGetStaticPropsType } from 'next'
import { getStaticPropsHandler } from 'storefront-modules/products/api'
import CustomStockLevel from '#/src/custom/products/CustomStockLevel'
import { ProductDetailPageStdConfig } from 'storefront-modules/products/components/ProductDetailPageStd'
import { useAihubAnalyticsVisitPageView } from 'storefront-modules/aihub'
import { useGtmViewProductPageTracker } from 'storefront-modules/data-collection'

type ProductDetailPageProps = InferGetStaticPropsType<typeof getStaticPropsHandler> &
  Pick<ProductDetailPageStdConfig, 'shippingConfig' | 'paymentProviderConfig'>

const ProductDetailPage: FC<ProductDetailPageProps> = ({
  product,
  bundles,
  variants,
  relatedGroups,
  badgesOptions,
  shippingConfig,
  paymentProviderConfig,
  deliveryOptions
}) => {
  const appConfig = useAppConfigContext()
  const { handleAddToCart, cartItems } = useCustomCart()
  const { openModal } = useModal()
  const windowState = useSelector(selectWindowState)
  const { handleProductRequest } = useProductRequest()

  useGtmViewProductPageTracker({
    categories: appConfig.categories,
    currency: appConfig.currency,
    product
  })
  useAihubAnalyticsVisitPageView({ product, cartItems })

  const openGallery = useCallback(
    (medias: ProductMedia[], selectedIndex: number) => {
      openModal<IModalPhotoGalleryProps>({
        title: '',
        componentId: 'openPhotoGallery',
        size: 'full',
        extraArgs: {
          medias,
          selectedMediaIndex: selectedIndex
        }
      })
    },
    [openModal]
  )

  return (
    <ProductDetailPageStd
      config={{
        bundles,
        variants,
        relatedGroups,
        product,
        badgesOptions,
        deliveryOptions,
        onAddToCart: handleAddToCart,
        onOpenMedias: openGallery,
        onProductRequest: handleProductRequest,
        windowState,
        deliveryRenderer: () => <CustomStockLevel {...{ product }} />,
        paths: {
          productsUrlPath: appConfig.paths.productsUrlPath
        },
        locale: appConfig.language,
        shippingConfig,
        paymentProviderConfig
      }}
    />
  )
}

export default ProductDetailPage
